/**
 * Entry point and router (docs/spec/05 §5). Everything is reachable by URL so an agent can drive it:
 *
 *   /                              the game
 *   /?play=supernova               straight into a game (play=supernova|classic|daily|demo)
 *      &stage=6 &seed=3 &lives=5   start stage, seed, fighters
 *   /?screen=<name>                any menu screen
 *   /?scene=sprites | paths        the art; the flight paths
 *   /?fx=authentic|enhanced        presentation preset for this load    &fresh=1  no saved data
 *   /?touch=on                     the touch controls, on any device
 *   /?freeze=1                     real time stops; only window.__game.advance moves the game
 */

import { createAudio } from '../audio';
import { createAutopilot } from '../bot';
import { Controls } from '../input/controls';
import { keyName } from '../input/keyNames';
import { isTouchDevice, TouchControls } from '../input/touch';
import { OptionsStore, withPreset } from '../platform/options';
import { ScoreBook } from '../platform/scoreBook';
import { browserStorage, memoryStorage } from '../platform/storage';
import { Display } from '../render/display/display';
import { Screen } from '../render/screen';
import { warmSprites } from '../render/sprites';
import type { Scene, Services } from '../ui/scene';
import { VERSION } from './brand';
import { installDebugApi } from './debugApi';
import { installFavicon } from './favicon';
import { createFactory } from './flow';
import { GameScene } from './game/gameScene';
import { App } from './host';
import { pathsScene, spritesScene } from './lookdev';
import { query } from './params';
import { bootRoute } from './routes';

function boot(): void {
  // Everything that is slow to make is made here, once, so that nothing hitches in play.
  warmSprites();
  installFavicon();
  const canvas = document.getElementById('screen') as HTMLCanvasElement;
  const live = document.getElementById('live');
  const storage = query.has('fresh') ? memoryStorage() : browserStorage;
  const options = new OptionsStore(storage);
  const fx = query.get('fx');
  if (fx === 'authentic' || fx === 'enhanced') options.override(withPreset(options.get(), fx));
  // ?touch=on shows the touch controls on a desktop, for looking at their layout.
  const touchParam = query.get('touch');
  if (touchParam === 'on' || touchParam === 'off') options.override({ touch: touchParam });

  const audio = createAudio();
  const applyVolumes = (): void => {
    const o = options.get();
    audio.setVolumes({ master: o.master / 10, sfx: o.sfx / 10, music: o.music / 10 });
  };
  applyVolumes();
  options.subscribe(applyVolumes);

  const controls = new Controls(options);
  const screen = new Screen();
  const display = new Display(canvas);
  let app: App | null = null;
  let touch: TouchControls | null = null;
  let touchWanted = false;
  let touchNova = true;
  const showTouch = (): void => {
    const mode = options.get().touch;
    const on = touchWanted && (mode === 'on' || (mode === 'auto' && isTouchDevice()));
    touch?.setVisible(on, touchNova);
    // On a tall phone the game sits at the top and the thumbs get the room below it.
    if (app) app.top = on && window.innerHeight > window.innerWidth * 1.4;
  };
  options.subscribe(showTouch);
  window.addEventListener('resize', showTouch);

  const services: Services = {
    audio,
    options,
    storage,
    scores: new ScoreBook(storage),
    input: { keyName, gamepad: () => controls.gamepad, touch: () => touch?.visible === true },
    announce(text) {
      if (live) live.textContent = text;
    },
    today() {
      // ?date=2026-10-08 pins the day, so tests and screenshots do not change at midnight.
      const pinned = /^(\d{4})-(\d{2})-(\d{2})$/.exec(query.get('date') ?? '');
      if (pinned) return { year: Number(pinned[1]), month: Number(pinned[2]), day: Number(pinned[3]) };
      const d = new Date();
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
    },
    version: VERSION,
  };

  const factory = createFactory({
    audio,
    controls,
    pilot(showman) {
      const bot = createAutopilot({ seed: Date.now() & 0xffff, skill: showman ? 0.8 : 1 });
      if (!showman) return (s) => bot.next(s);
      // Left to itself the bot shoots every wave down as it enters and the demo never shows a dive
      // or a beam. The showman lets every other wave pass and form up.
      return (s) => {
        const input = bot.next(s);
        return s.phase === 'entering' && s.spawner.wave % 2 === 0 ? { ...input, fire: false } : input;
      };
    },
    touch(visible, nova) {
      touchWanted = visible;
      touchNova = nova;
      showTouch();
    },
    seed: () => (Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0,
  });

  const scene = query.get('scene');
  const look: Scene | null = scene === 'sprites' ? spritesScene() : scene === 'paths' ? pathsScene() : null;
  app = new App(services, look ? () => look : factory, screen, display, controls);
  const running = app;
  touch = new TouchControls(controls.keys, (a) => running.scene?.action?.(a), () => audio.unlock());

  controls.attach({
    key: (k) => running.scene?.keydown?.(k) ?? false,
    action: (a) => running.scene?.action?.(a),
    any: () => audio.unlock(),
  });
  const pointer = (kind: 'move' | 'down' | 'up', e: PointerEvent): void => {
    if (kind === 'down') audio.unlock();
    const v = display.toVirtual(e.clientX, e.clientY);
    if (v) running.pointer({ x: v.x, y: v.y, kind, button: e.button, delta: 0 });
  };
  canvas.addEventListener('pointerdown', (e) => pointer('down', e));
  canvas.addEventListener('pointermove', (e) => pointer('move', e));
  canvas.addEventListener('pointerup', (e) => pointer('up', e));
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('wheel', (e) => {
    const v = display.toVirtual(e.clientX, e.clientY);
    if (v) running.pointer({ x: v.x, y: v.y, kind: 'wheel', button: 0, delta: Math.sign(e.deltaY) });
  }, { passive: true });

  const autoPause = (): void => {
    if (running.scene instanceof GameScene) running.scene.autoPause();
  };
  window.addEventListener('blur', autoPause);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) autoPause();
  });

  running.frozen = query.has('freeze');
  running.go(look ? { to: 'title' } : bootRoute());
  installDebugApi(running, controls);
  (window as unknown as { __music?: () => string[] }).__music = () => audio.tracks();
  running.run();
}

boot();
